import React from 'react';
import { motion } from 'framer-motion';

const CaseStudies = ({ id }) => {
  const blogPosts = [
    {
      title: "Powering Real-Time Broadcasting: The Tech Behind Eloelo's Billion-Event Broadcasting Streams",
      excerpt: "Deep dive into scaling real-time event streams for billion events across multiple live rooms. Covers Kafka, Flink, EMQX (MQTT), and AWS S3 for seamless real-time engagement at scale.",
      platform: "Medium",
      readTime: "5 min read",
      date: "Apr 7, 2025",
      url: "https://tech-blogs.eloelo.in/powering-real-time-broadcasting-the-tech-behind-eloelos-billion-event-broadcasting-streams-a77e16209956",
      tags: ["Event Driven Architecture", "Apache Kafka", "Flink Streaming", "MQTT Protocol", "Real-time Systems"],

      featured: true
    },
    {
      title: "How Eloelo Scaled Real-Time Virtual Gifting to Millions with EMQX on AWS EKS",
      excerpt: "Deep dive into scaling virtual gifting to millions of concurrent users using MQTT, EMQX clustering, and Kubernetes autoscaling. Covers infrastructure provisioning, session management, and cost optimization strategies.",
      platform: "Medium",
      readTime: "5 min read",
      date: "1 day ago",
      url: "https://medium.com/eloelo-tech-blogs/how-eloelo-scaled-real-time-virtual-gifting-to-millions-with-emqx-on-aws-eks-b9df2e2a42e1",
      tags: ["MQTT", "EMQX", "Kubernetes", "AWS EKS", "Real-time Systems"],

      featured: false
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <section id={id} className="section">
      <div className="container">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '4rem' }}
        >
          <h2 style={{ 
            fontSize: 'clamp(2rem, 4vw, 3rem)', 
            marginBottom: '1rem',
            color: 'var(--color-primary)'
          }}>
            Blog Showcase
          </h2>
          <p style={{ 
            fontSize: '1.2rem', 
            maxWidth: '600px', 
            margin: '0 auto',
            color: 'var(--color-secondary)'
          }}>
            Technical insights & architecture deep dives into scalable systems, real-time architectures, and engineering challenges
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '2rem'
          }}>
                      {blogPosts.map((post, index) => (
              <motion.div
                key={index}
                whileHover={{ 
                  y: -5,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                style={{
                  background: 'var(--color-bg-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '16px',
                  padding: '2rem',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                className="blog-card"
              >
              {/* Platform badge */}
              <div style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: post.platform === 'Medium' ? '#00AB6C' : '#1DA1F2',
                color: 'white',
                padding: '0.3rem 0.8rem',
                borderRadius: '20px',
                fontSize: '0.8rem',
                fontWeight: '600',
                zIndex: 10
              }}>
                {post.platform}
              </div>

              {/* Featured badge */}
              {post.featured && (
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  left: '1rem',
                  background: 'linear-gradient(135deg, #FF6B35, #F7931E)',
                  color: 'white',
                  padding: '0.3rem 0.8rem',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  zIndex: 10
                }}>
                  Featured
                </div>
              )}

              <div style={{ 
                marginTop: post.featured ? '3rem' : '1rem',
                paddingRight: '4rem'
              }}>
                {/* Title */}
                <h3 style={{ 
                  fontSize: '1.3rem', 
                  fontWeight: '700',
                  color: 'var(--color-primary)',
                  marginBottom: '1rem',
                  lineHeight: '1.4'
                }}>
                  {post.title}
                </h3>

                {/* Excerpt */}
                <p style={{ 
                  fontSize: '1rem', 
                  lineHeight: '1.6',
                  color: 'var(--color-secondary)',
                  marginBottom: '1.5rem'
                }}>
                  {post.excerpt}
                </p>

                {/* Tags */}
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '0.5rem',
                  marginBottom: '1.5rem'
                }}>
                  {post.tags.map((tag, tIndex) => (
                    <span key={tIndex} style={{
                      background: 'var(--color-bg-alt)',
                      color: 'var(--color-accent)',
                      padding: '0.3rem 0.8rem',
                      borderRadius: '20px',
                      fontSize: '0.8rem',
                      fontWeight: '500',
                      border: '1px solid var(--color-border)'
                    }}>
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Meta info */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  paddingTop: '1rem',
                  borderTop: '1px solid var(--color-border)'
                }}>
                  <div style={{
                    display: 'flex',
                    gap: '1rem',
                    fontSize: '0.9rem',
                    color: 'var(--color-secondary)'
                  }}>
                    <span>{post.readTime}</span>
                    <span>•</span>
                    <span>{post.date}</span>
                  </div>
                </div>

                {/* Read more button */}
                {post.url && (
                  <motion.a
                    href={post.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      marginTop: '1rem',
                      color: 'var(--color-accent)',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '0.9rem'
                    }}
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    Read on Medium →
                  </motion.a>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>


      </div>
    </section>
  );
};

export default CaseStudies; 